import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
  Polyline,
  Polygon,
  TransitLayer,
} from "@react-google-maps/api";
import { toast } from "react-toastify";
import { getData, postDataWithBody, putDataWithBody } from "../../../Services/Services";
import {
  createroutefleet,
  updateRoute,
  getThemeURL,
  findAllGeofenceChildrenRegionsById,
  gisCoordinatesData,
  gisBlockCoordinatesData,
  gisStateCoordinates,
} from "../../../Services/EndPoints";
import Tree from "rc-tree";
import "rc-tree/assets/index.css";
import LoadingOverlay from "./LoadingOverlay";
import { useLocation } from "react-router-dom";
import "./RouteEnhancements.css";

// ─── Shared Google Maps loader (fixes "Loader must not be called again" error) ─
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_LOADER_ID,
} from "../../../../src/Pages/DMS/GpsTrackManagement/Googlemapsloader";

// ─── Hide Google's native InfoWindow close button (we use our own) ────────────
const infoWindowStyle = `
  .gm-ui-hover-effect { display: none !important; }
  .gm-style-iw-chr   { display: none !important; }
  .gm-style-iw-d     { overflow: hidden !important; padding: 0 !important; }
  .gm-style-iw-c     { padding: 0 !important; border-radius: 8px !important; box-shadow: 0 2px 10px rgba(0,0,0,0.2) !important; }
  .gm-style-iw       { padding: 0 !important; border-radius: 8px !important; }
`;

// ─── Map config ───────────────────────────────────────────────────────────────
const MAP_CONTAINER_STYLE = { height: "600px", width: "100%" };
const DEFAULT_CENTER = { lat: 17.385044, lng: 78.486671 };

const MAP_OPTIONS = {
  mapTypeControl: false,
  streetViewControl: true,
  fullscreenControl: true,
  zoomControl: true,
  clickableIcons: false,
  gestureHandling: 'greedy',
};

const mapTypes = {
  road: "roadmap",
  satellite: "satellite",
  terrain: "terrain",
  hybrid: "hybrid",
};

// ─── Layer options — inline SVGs only (no lucide-react needed) ────────────────
const LAYERS = [
  {
    id: "road", label: "Standard",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg>,
  },
  {
    id: "satellite", label: "Satellite",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  },
  {
    id: "terrain", label: "Terrain",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 18 9 6 13 13 16 9 21 18"/></svg>,
  },
  {
    id: "hybrid", label: "Hybrid",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><line x1="2" y1="12" x2="22" y2="12"/></svg>,
  },
];

// ─── Inline Layers button icon ────────────────────────────────────────────────
const LayersIcon = ({ color = "#475569" }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 2 7 12 12 22 7 12 2"/>
    <polyline points="2 17 12 22 22 17"/>
    <polyline points="2 12 12 17 22 12"/>
  </svg>
);

// ─── Google Maps style pin icons (SVG data URI) ───────────────────────────────
const makePinSvg = (fillColor) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="44" viewBox="0 0 28 44">
    <path d="M14 0C6.268 0 0 6.268 0 14c0 5.817 3.412 10.83 8.35 13.269L14 44l5.65-16.731C24.588 24.83 28 19.817 28 14 28 6.268 21.732 0 14 0z"
      fill="${fillColor}" stroke="white" stroke-width="1.5"/>
    <circle cx="14" cy="14" r="5.5" fill="white" fill-opacity="0.85"/>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const makeHaltSvg = () => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34">
    <circle cx="17" cy="17" r="16" fill="#FBBC04" stroke="white" stroke-width="2.5"/>
    <rect x="11" y="10" width="4" height="14" rx="1.5" fill="white"/>
    <rect x="19" y="10" width="4" height="14" rx="1.5" fill="white"/>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const makeDotSvg = () => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
    <circle cx="9" cy="9" r="8" fill="#17a2b8" stroke="white" stroke-width="2.5"/>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const createCustomIcon = (color, type) => {
  if (!window.google) return undefined;
  if (type === "waypoint") {
    return {
      url: makeDotSvg(),
      scaledSize: new window.google.maps.Size(18, 18),
      anchor: new window.google.maps.Point(9, 9),
    };
  }
  if (type === "halt") {
    return {
      url: makeHaltSvg(),
      scaledSize: new window.google.maps.Size(34, 34),
      anchor: new window.google.maps.Point(17, 17),
    };
  }
  const pinColor = type === "start" ? "#34A853" : "#EA4335";
  return {
    url: makePinSvg(pinColor),
    scaledSize: new window.google.maps.Size(28, 44),
    anchor: new window.google.maps.Point(14, 44),
  };
};

const getIconColor = (type) => {
  switch (type) {
    case "start": return "#28a745";
    case "end": return "#dc3545";
    case "halt": return "#ffc107";
    default: return "#1a7a73";
  }
};

// ─── Haversine distance (km) ──────────────────────────────────────────────────
const haversine = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// ─── Component ────────────────────────────────────────────────────────────────
const CreateRoute = ({
  userId,
  editMode = false,
  editRouteData = null,
  editRoutePoints = [],
  onUpdateSuccess,
}) => {
  // ── Shared loader — same id + libraries as TripAction & GpsTrackingMap ────
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_LOADER_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // ── State ──────────────────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false);
  const [routeName, setRouteName] = useState("");
  const [fromLocation, setFromLocation] = useState("");
  const [toLocation, setToLocation] = useState("");
  const [selectedPointType, setSelectedPointType] = useState("");
  const [selectedLayer, setSelectedLayer] = useState("road");
  const [showLayerControl, setShowLayerControl] = useState(false);
  const [showTransit, setShowTransit] = useState(false);

  const location = useLocation();
  const isEdit = location.state?.editRoute;
  const { editRoute, routeData, routePoints } = location.state || {};

  const [points, setPoints] = useState(
    editMode && editRoutePoints
      ? editRoutePoints.map((p) => ({
        id: p.id || Date.now(),
        latitude: parseFloat(p.latitude),
        longitude: parseFloat(p.longitude),
        address: p.address || "",
        isLoadingAddress: false,
        pointType: p.pointType?.toLowerCase() || "halt",
        sequenceNo: p.sequenceNo || 0,
        waitTime: p.waitTime || 5,
        createdBy: userId,
        created_on: p.created_on || new Date().toISOString().slice(0, 19).replace("T", " ") + ".000",
      }))
      : []
  );

  const [waitTimeModal, setWaitTimeModal] = useState({ show: false, pointId: null, currentWaitTime: 0 });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [roadSnappingEnabled, setRoadSnappingEnabled] = useState(false);
  const [snappedRouteCoordinates, setSnappedRouteCoordinates] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [previousSnappedRoute, setPreviousSnappedRoute] = useState([]);
  const [isSnapping, setIsSnapping] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [waypoints, setWaypoints] = useState([]);
  const [hasModifiedRoute, setHasModifiedRoute] = useState(false);
  const [dragHistory, setDragHistory] = useState([]);
  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ show: false, pointId: null, pointType: null });

  // ── Region tree state ─────────────────────────────────────────────────────
  const [regionTreeCollapsed, setRegionTreeCollapsed] = useState(true);
  const [stateId, setStateId] = useState(82);
  const [stateName, setStateName] = useState('STATE');
  const [regionTreeData, setRegionTreeData] = useState([]);
  const [regionExpandedKeys, setRegionExpandedKeys] = useState([]);
  const [regionCheckedKeys, setRegionCheckedKeys] = useState([]);
  const [districtPolygons, setDistrictPolygons] = useState([]);
  const [regionCheckLoading, setRegionCheckLoading] = useState(false);
  const [selectedOgcFid, setSelectedOgcFid] = useState(null);
  const [selectedOgcType, setSelectedOgcType] = useState('');
  const [selectedRegionId, setSelectedRegionId] = useState(null);
  const [regionSearchText, setRegionSearchText] = useState('');
  const regionDataMapRef = useRef(new Map());

  const mapRef = useRef(null);
  const geocoderRef = useRef(null);
  const autocompleteRef = useRef(null);
  const directionsRef = useRef(null);

  const fromLocationTimerRef = useRef();
  const toLocationTimerRef = useRef();
  const searchTimerRef = useRef();

  // ── Init Google singletons on map load ────────────────────────────────────
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    geocoderRef.current = new window.google.maps.Geocoder();
    autocompleteRef.current = new window.google.maps.places.AutocompleteService();
    directionsRef.current = new window.google.maps.DirectionsService();
    map.setOptions({
      mapTypeControlOptions: {
        style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: window.google.maps.ControlPosition.TOP_RIGHT,
        mapTypeIds: [
          window.google.maps.MapTypeId.ROADMAP,
          window.google.maps.MapTypeId.SATELLITE,
          window.google.maps.MapTypeId.TERRAIN,
          window.google.maps.MapTypeId.HYBRID,
        ],
      },
    });
  }, []);

  // ── Zoom to fit all route points ─────────────────────────────────────────
  const handleZoomToRoute = useCallback(() => {
    if (!mapRef.current || points.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend({ lat: p.latitude, lng: p.longitude }));
    mapRef.current.fitBounds(bounds, { top: 60, right: 20, bottom: 20, left: 20 });
  }, [points]);

  // ── Cleanup timers ────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      [fromLocationTimerRef, toLocationTimerRef, searchTimerRef].forEach(
        (r) => r.current && clearTimeout(r.current)
      );
    };
  }, []);

  // ── Fetch user profile to get state info ─────────────────────────────────
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await getData(getThemeURL);
        if (response && Array.isArray(response) && response.length > 0) {
          setStateName(response[0].statename || 'TELANGANA');
        }
      } catch (err) {
        console.error("Error fetching user profile:", err);
      }
    };
    fetchUserProfile();
  }, []);

  // ── Init region tree eagerly ──────────────────────────────────────────────
  useEffect(() => {
    if (!stateId) return;
    const stateKey = String(stateId);
    regionDataMapRef.current.set(stateKey, { id: stateId, name: stateName, level: 0, gis_id: null });
    setRegionTreeData([{ title: stateName, key: stateKey, checkable: true }]);
    setRegionExpandedKeys([]);
    setRegionCheckedKeys([]);
    setDistrictPolygons([]);

    postDataWithBody(findAllGeofenceChildrenRegionsById, [String(stateId)], {})
      .then(children => {
        const childNodes = (Array.isArray(children) ? children : []).map(region => {
          const childKey = `${stateKey}-${region.id}`;
          regionDataMapRef.current.set(childKey, {
            id: region.id, name: region.name,
            gis_id: region.gis_id, region_type_id: region.region_type_id, level: 1
          });
          return { title: region.name, key: childKey, isLeaf: false, checkable: true };
        });
        setRegionTreeData([{ title: stateName, key: stateKey, checkable: true, children: childNodes }]);
        setRegionExpandedKeys([stateKey]);
      })
      .catch(err => console.error('Failed to load district children:', err));
  }, [stateId, stateName]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Tree helpers ──────────────────────────────────────────────────────────
  const addChildrenToTree = (nodes, targetKey, children) =>
    nodes.map(node => {
      if (node.key === targetKey) return { ...node, children };
      if (node.children) return { ...node, children: addChildrenToTree(node.children, targetKey, children) };
      return node;
    });

  const geoJsonToGooglePaths = (geoJson) => {
    if (!geoJson) return [];
    if (geoJson.type === 'Polygon')
      return geoJson.coordinates.map(ring => ring.map(([lng, lat]) => ({ lat, lng })));
    if (geoJson.type === 'MultiPolygon')
      return geoJson.coordinates.flatMap(poly => poly.map(ring => ring.map(([lng, lat]) => ({ lat, lng }))));
    return [];
  };

  const polygonColorByLevel = (level) => {
    if (level === 0) return { stroke: '#1a7a73', fill: '#1a7a73', opacity: 0.15 };
    if (level === 1) return { stroke: '#10b981', fill: '#10b981', opacity: 0.18 };
    return { stroke: '#f97316', fill: '#f97316', opacity: 0.22 };
  };

  const levelToOgcType = (level) => {
    if (level === 0) return 'state';
    if (level === 1) return 'district';
    return 'block';
  };

  // ── Lazy-load children in region tree ────────────────────────────────────
  const loadRegionChildren = async (node) => {
    const key = node.key;
    const parts = key.split('-');
    const nodeId = parts[parts.length - 1];
    const level = parts.length - 1;
    try {
      const children = await postDataWithBody(findAllGeofenceChildrenRegionsById, [nodeId], {});
      const childNodes = (Array.isArray(children) ? children : []).map(region => {
        const childKey = `${key}-${region.id}`;
        const childLevel = level + 1;
        regionDataMapRef.current.set(childKey, {
          id: region.id, name: region.name,
          gis_id: region.gis_id, region_type_id: region.region_type_id, level: childLevel
        });
        return { title: region.name, key: childKey, isLeaf: childLevel >= 2, checkable: true };
      });
      setRegionTreeData(prev => addChildrenToTree(prev, key, childNodes));
    } catch (err) {
      console.error('Failed to load region children:', err);
    }
  };

  // ── Check/uncheck region node ─────────────────────────────────────────────
  const onRegionCheck = async (checkedKeysValue, info) => {
    const checked = Array.isArray(checkedKeysValue)
      ? checkedKeysValue
      : (checkedKeysValue.checked || []);

    const nodeKey = info.node.key;
    const nodeData = regionDataMapRef.current.get(nodeKey);
    if (!nodeData) return;

    const level = nodeData.level ?? 1;

    setRegionCheckLoading(true);
    try {
    if (info.checked) {
      const gisParam = level === 0 ? nodeData.id : nodeData.gis_id;
      setSelectedOgcType(levelToOgcType(level));
      setSelectedRegionId(nodeData.id != null ? nodeData.id : null);

      let ownPolygon = null;
      if (gisParam) {
        try {
          const geoEndpoint = level === 0
            ? gisStateCoordinates
            : level >= 2 ? gisBlockCoordinatesData : gisCoordinatesData;
          const res = await getData(geoEndpoint + gisParam);
          if (res && res.length > 0) {
            setSelectedOgcFid(res[0].ogc_fid ?? gisParam);
            const paths = geoJsonToGooglePaths(JSON.parse(res[0].coordinates));
            ownPolygon = { gisId: gisParam, paths, name: nodeData.name, level };
          }
        } catch (err) {
          console.error('Failed to fetch region geometry:', err);
        }
      }

      let blockCheckedKeys = [];
      let blockPolygons = [];
      if (level === 1) {
        try {
          const blocks = await postDataWithBody(findAllGeofenceChildrenRegionsById, [String(nodeData.id)], {});
          const blockNodes = (Array.isArray(blocks) ? blocks : []).map(block => {
            const blockKey = `${nodeKey}-${block.id}`;
            regionDataMapRef.current.set(blockKey, {
              id: block.id, name: block.name,
              gis_id: block.gis_id, region_type_id: block.region_type_id, level: 2
            });
            return { title: block.name, key: blockKey, isLeaf: true, checkable: true };
          });
          setRegionTreeData(prev => addChildrenToTree(prev, nodeKey, blockNodes));
          setRegionExpandedKeys(prev => prev.includes(nodeKey) ? prev : [...prev, nodeKey]);
          blockCheckedKeys = blockNodes.map(n => n.key);
          const results = await Promise.all(
            blockNodes.map(async (bn) => {
              const bd = regionDataMapRef.current.get(bn.key);
              if (!bd?.gis_id) return null;
              try {
                const res = await getData(gisBlockCoordinatesData + bd.gis_id);
                if (res && res.length > 0) {
                  const paths = geoJsonToGooglePaths(JSON.parse(res[0].coordinates));
                  return { gisId: bd.gis_id, paths, name: bd.name, level: 2 };
                }
              } catch { /* skip */ }
              return null;
            })
          );
          blockPolygons = results.filter(Boolean);
        } catch (err) {
          console.error('Failed to auto-load blocks for district:', err);
        }
      }

      setRegionCheckedKeys([...checked, ...blockCheckedKeys]);
      setDistrictPolygons(prev => {
        const blockGisIds = blockPolygons.map(p => p.gisId);
        const filtered = prev
          .filter(p => gisParam ? p.gisId !== gisParam : true)
          .filter(p => !blockGisIds.includes(p.gisId));
        return [...filtered, ...(ownPolygon ? [ownPolygon] : []), ...blockPolygons];
      });

      const allPaths = [
        ...(ownPolygon ? ownPolygon.paths : []),
        ...blockPolygons.flatMap(p => p.paths)
      ];
      if (allPaths.length > 0 && mapRef.current) {
        const bounds = new window.google.maps.LatLngBounds();
        allPaths.forEach(ring => ring.forEach(pt => bounds.extend(pt)));
        mapRef.current.fitBounds(bounds, 30);
      }
    } else {
      const gisParam = level === 0 ? nodeData.id : nodeData.gis_id;
      const blockGisIds = [];
      const blockKeysToRemove = [];
      if (level === 1) {
        regionDataMapRef.current.forEach((data, key) => {
          if (key.startsWith(nodeKey + '-') && data.level === 2) {
            blockKeysToRemove.push(key);
            if (data.gis_id) blockGisIds.push(data.gis_id);
          }
        });
      }
      const remainingKeys = checked.filter(k => !blockKeysToRemove.includes(k));
      setRegionCheckedKeys(remainingKeys);
      setDistrictPolygons(prev =>
        prev.filter(p => p.gisId !== gisParam && !blockGisIds.includes(p.gisId))
      );
      if (remainingKeys.length === 0) {
        setSelectedOgcFid(null);
        setSelectedOgcType('');
        setSelectedRegionId(null);
      }
    }
    } finally {
      setRegionCheckLoading(false);
    }
  };

  // ── fitBounds ─────────────────────────────────────────────────────────────
  const fitMapToRoute = useCallback(
    (customPoints) => {
      const pts = customPoints || points;
      if (!mapRef.current || pts.length < 2) return;
      try {
        const bounds = new window.google.maps.LatLngBounds();
        pts.forEach((p) =>
          bounds.extend({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) })
        );
        mapRef.current.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      } catch (e) {
        console.error("fitMapToRoute:", e);
      }
    },
    [points]
  );

  // ── Reverse geocode ───────────────────────────────────────────────────────
  const reverseGeocode = useCallback((lat, lng) => {
    return new Promise((resolve) => {
      if (!geocoderRef.current) { resolve(null); return; }
      geocoderRef.current.geocode(
        { location: { lat: parseFloat(lat), lng: parseFloat(lng) } },
        (results, status) => {
          if (status === "OK" && results[0]) {
            const components = results[0].address_components;
            const get = (...types) => {
              for (const type of types) {
                const c = components.find((ac) => ac.types.includes(type));
                if (c) return c.long_name;
              }
              return null;
            };
            const road = get("route", "premise", "establishment");
            const area = get("sublocality_level_1", "sublocality", "neighborhood");
            const city = get("locality", "administrative_area_level_3");
            const state = get("administrative_area_level_1");
            const pincode = get("postal_code");
            const parts = [road, area, city, state, pincode].filter(Boolean);
            resolve(parts.length ? parts.join(", ") : results[0].formatted_address);
          } else {
            resolve(null);
          }
        }
      );
    });
  }, []);

  // ── Forward geocode + pan ─────────────────────────────────────────────────
  const geocodeAndZoomToLocation = useCallback(
    async (locationName, fieldType, isAutoZoom = false) => {
      if (!locationName.trim() || locationName.trim().length < 3) return;
      if (!geocoderRef.current) return;
      setIsGeocoding(true);
      geocoderRef.current.geocode(
        { address: locationName, componentRestrictions: { country: "IN" } },
        (results, status) => {
          setIsGeocoding(false);
          if (status === "OK" && results[0]) {
            const loc = results[0].geometry.location;
            if (mapRef.current) {
              if (results[0].geometry.viewport) {
                mapRef.current.fitBounds(results[0].geometry.viewport);
              } else {
                mapRef.current.setCenter({ lat: loc.lat(), lng: loc.lng() });
                mapRef.current.setZoom(15);
              }
            }
            if (!isAutoZoom)
              toast.success(
                `Map centred on ${fieldType}: ${results[0].formatted_address.split(",")[0]}`,
                { position: "top-center", autoClose: 2000 }
              );
          }
        }
      );
    },
    []
  );

  // ── Search suggestions ────────────────────────────────────────────────────
  const searchLocation = useCallback((query) => {
    if (!query.trim()) { setSearchResults([]); return; }
    if (!autocompleteRef.current) return;
    setIsSearching(true);
    autocompleteRef.current.getPlacePredictions(
      { input: query, componentRestrictions: { country: "in" }, types: ["geocode", "establishment"] },
      (predictions, status) => {
        setIsSearching(false);
        const OK = window.google.maps.places.PlacesServiceStatus.OK;
        if (status === OK && predictions?.length) {
          setSearchResults(predictions.map((p) => ({ id: p.place_id, name: p.description, placeId: p.place_id })));
        } else {
          setSearchResults([]);
        }
      }
    );
  }, []);

  // ── Select search result ──────────────────────────────────────────────────
  const selectSearchResult = useCallback((result) => {
    setSearchQuery(result.name);
    setSearchResults([]);
    if (!geocoderRef.current) return;
    geocoderRef.current.geocode({ placeId: result.placeId }, (results, status) => {
      if (status === "OK" && results[0]) {
        const loc = results[0].geometry.location;
        if (mapRef.current) {
          if (results[0].geometry.viewport) {
            mapRef.current.fitBounds(results[0].geometry.viewport);
          } else {
            mapRef.current.setCenter({ lat: loc.lat(), lng: loc.lng() });
            mapRef.current.setZoom(15);
          }
        }
        toast.success(`Map centred on: ${result.name.split(",")[0]}`);
      }
    });
  }, []);

  // ── Road snapping ─────────────────────────────────────────────────────────
  const snapPointsToRoad = useCallback(async (pts) => {
    if (!directionsRef.current || pts.length < 2) return pts.map((p) => [p.lat, p.lng]);

    const decodeResult = (result) => {
      const coords = [];
      result.routes[0].legs.forEach((leg) => {
        leg.steps.forEach((step) => {
          window.google.maps.geometry.encoding
            .decodePath(step.polyline.points)
            .forEach((ll) => coords.push([ll.lat(), ll.lng()]));
        });
      });
      return coords;
    };

    const requestSegment = (segment) =>
      new Promise((resolve) => {
        const origin = new window.google.maps.LatLng(segment[0].lat, segment[0].lng);
        const destination = new window.google.maps.LatLng(segment[segment.length - 1].lat, segment[segment.length - 1].lng);
        const waypoints = segment.slice(1, -1).map((p) => ({
          location: new window.google.maps.LatLng(p.lat, p.lng),
          stopover: p.pointType === "halt",
        }));
        directionsRef.current.route(
          { origin, destination, waypoints, travelMode: window.google.maps.TravelMode.DRIVING, optimizeWaypoints: false },
          (result, status) => {
            if (status === "OK") resolve({ ok: true, coords: decodeResult(result) });
            else resolve({ ok: false, coords: segment.map((p) => [p.lat, p.lng]) });
          }
        );
      });

    const MAX_WP = 23;
    if (pts.length <= MAX_WP + 2) {
      const { coords } = await requestSegment(pts);
      return coords;
    }

    const all = [];
    let i = 0;
    while (i < pts.length - 1) {
      const chunk = pts.slice(i, i + MAX_WP + 2);
      const { coords } = await requestSegment(chunk);
      if (all.length > 0) coords.shift();
      all.push(...coords);
      i += MAX_WP + 1;
    }
    return all;
  }, []);

  // ── Geocode edit-mode points ──────────────────────────────────────────────
  const geocodeEditedPoints = useCallback(
    async (pointsToGeocode) => {
      const results = await Promise.allSettled(
        pointsToGeocode.map(async (point, i) => {
          await new Promise((r) => setTimeout(r, i * 300));
          const address = await reverseGeocode(parseFloat(point.latitude), parseFloat(point.longitude));
          return {
            ...point,
            address: address || `${parseFloat(point.latitude).toFixed(6)}, ${parseFloat(point.longitude).toFixed(6)}`,
            isLoadingAddress: false,
          };
        })
      );
      return results.filter((r) => r.status === "fulfilled").map((r) => r.value);
    },
    [reverseGeocode]
  );

  // ── Edit-mode init ────────────────────────────────────────────────────────
  useEffect(() => {
    if (editRoute && routeData) {
      setRouteName(routeData.routeName);
      setFromLocation(routeData.fromLocation);
      setToLocation(routeData.toLocation);
    }
    if (routePoints?.length) setPoints(routePoints);
  }, [editRoute, routeData, routePoints]);

  useEffect(() => {
    if (!(editRoute && routeData && routePoints?.length && isLoaded)) return;
    setRouteName(routeData.routeName);
    setFromLocation(routeData.fromLocation);
    setToLocation(routeData.toLocation);

    const keyPoints = routePoints.filter((p) => {
      const t = p.pointType?.toLowerCase();
      return t === "start" || t === "halt" || t === "end";
    });

    const withPlaceholders = keyPoints.map((p) => ({
      id: p.id || Date.now(),
      latitude: parseFloat(p.latitude),
      longitude: parseFloat(p.longitude),
      address: `${parseFloat(p.latitude).toFixed(6)}, ${parseFloat(p.longitude).toFixed(6)}`,
      isLoadingAddress: true,
      pointType: p.pointType?.toLowerCase() || "halt",
      sequenceNo: p.sequenceNo || 0,
      waitTime: p.waitTime || 5,
      createdBy: userId,
      created_on: p.created_on || new Date().toISOString().slice(0, 19).replace("T", " ") + ".000",
    }));

    setPoints(withPlaceholders);
    setTimeout(() => fitMapToRoute(withPlaceholders), 300);
    geocodeEditedPoints(withPlaceholders).then((geocoded) => setPoints(geocoded));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editRoute, routeData, routePoints, userId, isLoaded]);

  // ── Form field handlers ───────────────────────────────────────────────────
  const validateRouteNameInput = (v) => /^[a-zA-Z0-9\s-]*$/.test(v);
  const validateLocationInput = (v) => /^[a-zA-Z0-9\s,.\-\/()#&']*$/.test(v);

  const handleRouteNameChange = (e, setter, fieldName) => {
    const { value } = e.target;
    if (validateRouteNameInput(value)) setter(value);
    else toast.error(`${fieldName} can only contain letters, numbers, spaces, and hyphens`, { position: "top-center" });
  };

  const handleLocationChange = (e, setter, fieldName) => {
    const { value } = e.target;
    if (!validateLocationInput(value)) {
      toast.error(`${fieldName} contains invalid characters`, { position: "top-center" });
      return;
    }
    setter(value);
    const timerRef = fieldName === "From Location" ? fromLocationTimerRef : toLocationTimerRef;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (value.trim().length >= 3) geocodeAndZoomToLocation(value, fieldName, true);
    }, 800);
  };

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => searchLocation(query), 500);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  };

  // ── Point-type guards ─────────────────────────────────────────────────────
  const isPointTypeEnabled = useCallback(
    (pointType) => {
      const hasStart = points.some((p) => p.pointType === "start");
      const hasEnd = points.some((p) => p.pointType === "end");
      switch (pointType) {
        case "start": return !hasStart && routeName.trim() !== "" && fromLocation.trim() !== "";
        case "halt": return hasStart && (!hasEnd || isEdit || editMode);
        case "end": return hasStart && !hasEnd && (toLocation.trim() !== "" || isEdit || editMode);
        default: return false;
      }
    },
    [points, routeName, fromLocation, toLocation, isEdit, editMode]
  );

  const isCreateRouteEnabled = () => points.some((p) => p.pointType === "end") && points.length >= 2;

  const getNextSequenceNo = useCallback(() => {
    if (!points.length) return 1;
    return Math.max(...points.map((p) => p.sequenceNo)) + 1;
  }, [points]);

  // ── Check if a lat/lng is inside the selected region polygons ────────────
  const isPointInsideRegion = useCallback((lat, lng) => {
    if (!districtPolygons.length || !window.google) return true;
    const point = new window.google.maps.LatLng(lat, lng);
    return districtPolygons.some(dp =>
      dp.paths.some(path => {
        const polygon = new window.google.maps.Polygon({ paths: path });
        return window.google.maps.geometry.poly.containsLocation(point, polygon);
      })
    );
  }, [districtPolygons]);

  // ── Add point on map click ────────────────────────────────────────────────
  const handleMapClick = useCallback(
    async (latlng, pointType) => {
      if (!pointType) { toast.warning("Please select a point type first"); return; }
      if (!isPointTypeEnabled(pointType)) {
        if (pointType === "halt" && !points.some((p) => p.pointType === "start"))
          toast.warning("Please add a start point first");
        return;
      }
      if (pointType === "start" && points.some((p) => p.pointType === "start")) {
        toast.warning("Start point already exists. Remove it first to add a new one.");
        return;
      }
      if (pointType === "end" && points.some((p) => p.pointType === "end")) {
        toast.warning("End point already exists. Remove it first to add a new one.");
        return;
      }
      if (!isPointInsideRegion(latlng.lat, latlng.lng)) {
        toast.warning("Please place points within the selected region boundary");
        return;
      }

      const newPoint = {
        id: Date.now(),
        latitude: latlng.lat,
        longitude: latlng.lng,
        address: null,
        isLoadingAddress: true,
        pointType,
        sequenceNo: pointType === "start" ? 1 : getNextSequenceNo(),
        waitTime: pointType === "halt" ? 5 : 0,
        createdBy: 1,
        created_on: new Date().toISOString().slice(0, 19).replace("T", " ") + ".000",
      };

      if (pointType === "start" && points.length > 0) {
        setPoints([newPoint, ...points.map((p, i) => ({ ...p, sequenceNo: i + 2 }))]);
      } else {
        setPoints((prev) => [...prev, newPoint]);
      }

      toast.success(`${pointType.charAt(0).toUpperCase() + pointType.slice(1)} point added successfully`);
      const address = await reverseGeocode(latlng.lat, latlng.lng);
      setPoints((prev) =>
        prev.map((p) => (p.id === newPoint.id ? { ...p, address, isLoadingAddress: false } : p))
      );
      if (pointType === "end") setSelectedPointType("");
    },
    [points, isPointTypeEnabled, getNextSequenceNo, reverseGeocode, isPointInsideRegion]
  );

  const onGoogleMapClick = useCallback(
    (e) => {
      if (roadSnappingEnabled) return;
      if (!selectedPointType || !isPointTypeEnabled(selectedPointType)) return;
      handleMapClick({ lat: e.latLng.lat(), lng: e.latLng.lng() }, selectedPointType);
    },
    [roadSnappingEnabled, selectedPointType, isPointTypeEnabled, handleMapClick]
  );

  // ── Waypoint generation ───────────────────────────────────────────────────
  const generateWaypoints = (routeCoords) => {
    if (routeCoords.length < 2) return [];
    const step = Math.floor(routeCoords.length / 4);
    const list = [];
    for (let i = 1; i <= 3; i++) {
      const idx = step * i;
      if (idx < routeCoords.length - step)
        list.push({ id: `waypoint-${idx}`, position: routeCoords[idx], index: idx });
    }
    return list;
  };

  // ── Toggle road snapping ──────────────────────────────────────────────────
  const toggleRoadSnapping = async () => {
    if (points.length < 2) { toast.warning("Add at least 2 points to enable road snapping"); return; }
    const newVal = !roadSnappingEnabled;
    setRoadSnappingEnabled(newVal);
    if (newVal) {
      setIsSnapping(true);
      toast.info("Snapping route to roads...", { autoClose: 2000 });
      const coordsForSnap = [...points]
        .sort((a, b) => a.sequenceNo - b.sequenceNo)
        .map((p) => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude), pointType: p.pointType }));
      try {
        const snapped = await snapPointsToRoad(coordsForSnap);
        if (snapped?.length >= 2) {
          setSnappedRouteCoordinates(snapped);
          setHasModifiedRoute(false);
          setWaypoints(generateWaypoints(snapped));
          toast.success("Route Created Successfully", { autoClose: 4000 });
          setTimeout(fitMapToRoute, 100);
        } else {
          const orig = [...points].sort((a, b) => a.sequenceNo - b.sequenceNo).map((p) => [parseFloat(p.latitude), parseFloat(p.longitude)]);
          setSnappedRouteCoordinates(orig);
          setWaypoints([]);
          toast.warning("Using direct route");
          setTimeout(fitMapToRoute, 100);
        }
      } catch {
        const orig = [...points].sort((a, b) => a.sequenceNo - b.sequenceNo).map((p) => [parseFloat(p.latitude), parseFloat(p.longitude)]);
        setSnappedRouteCoordinates(orig);
        setWaypoints([]);
        toast.error("Road snapping failed", { autoClose: 2000 });
        setTimeout(fitMapToRoute, 100);
      }
      setIsSnapping(false);
    } else {
      setSnappedRouteCoordinates([]);
      setWaypoints([]);
      setHasModifiedRoute(false);
      toast.info("Showing direct route");
    }
  };

  // ── Undo drag ─────────────────────────────────────────────────────────────
  const resetToPreviousRoute = () => {
    if (!dragHistory.length) { toast.warning("No previous route state available"); return; }
    const prev = dragHistory[dragHistory.length - 1];
    setSnappedRouteCoordinates([...prev]);
    setWaypoints(generateWaypoints(prev));
    setHasModifiedRoute(true);
    setDragHistory((h) => h.slice(0, -1));
    toast.success("Route reverted to previous drag state!", { autoClose: 2000 });
  };

  // ── Waypoint drag-end ─────────────────────────────────────────────────────
  const handleWaypointDragEnd = async (waypointId, newPosition) => {
    setIsSnapping(true);
    toast.info("Adjusting route to new path...", { autoClose: 2000 });
    setDragHistory((prev) => [...prev.slice(-4), [...snappedRouteCoordinates]]);
    const routePts = [...points]
      .sort((a, b) => a.sequenceNo - b.sequenceNo)
      .map((p) => ({ lat: p.latitude, lng: p.longitude, pointType: p.pointType }));
    const wpObj = waypoints.find((w) => w.id === waypointId);
    if (wpObj && snappedRouteCoordinates.length > 0) {
      const rel = wpObj.index / snappedRouteCoordinates.length;
      const idx = Math.max(1, Math.min(routePts.length - 1, Math.floor(rel * routePts.length)));
      routePts.splice(idx, 0, { lat: newPosition[0], lng: newPosition[1], pointType: "waypoint" });
    }
    try {
      const newSnapped = await snapPointsToRoad(routePts);
      if (newSnapped?.length >= 2) {
        setSnappedRouteCoordinates(newSnapped);
        setWaypoints(generateWaypoints(newSnapped));
        setHasModifiedRoute(true);
        toast.success("Route adjusted! Click undo to revert.", { autoClose: 3000 });
      } else {
        toast.warning("Could not adjust route, keeping current");
      }
    } catch {
      toast.error("Failed to adjust route", { autoClose: 2000 });
    } finally {
      setIsSnapping(false);
    }
  };

  // ── Point removal ─────────────────────────────────────────────────────────
  const clearRouteState = () => { setSnappedRouteCoordinates([]); setWaypoints([]); setHasModifiedRoute(false); };
  const reSeq = (arr) => arr.map((p, i) => ({ ...p, sequenceNo: i + 1 }));

  const removePoint = (pointId) => {
    const pt = points.find((p) => p.id === pointId);
    if (pt.pointType === "start") {
      if (!isEdit && !editMode) setFromLocation("");
      setPoints(reSeq(points.filter((p) => p.id !== pointId)));
      setSelectedPointType(""); clearRouteState();
      toast.info("Start point removed. You can now add a new start point.", { autoClose: 3000 });
      return;
    }
    if (pt.pointType === "halt") {
      setPoints(reSeq(points.filter((p) => p.id !== pointId)));
      setSelectedPointType(""); clearRouteState();
      toast.info("Halt point removed.", { autoClose: 3000 });
      return;
    }
    if (pt.pointType === "end") {
      if (!isEdit && !editMode) setToLocation("");
      setPoints(reSeq(points.filter((p) => p.id !== pointId)));
      setSelectedPointType(""); clearRouteState();
      toast.info("End point removed");
      return;
    }
    setPoints(reSeq(points.filter((p) => p.id !== pointId)));
    toast.info("Point removed successfully");
  };

  const handleConfirmDelete = () => {
    setPoints([]); setSelectedPointType("");
    setRoadSnappingEnabled(false); clearRouteState();
    setDeleteConfirmModal({ show: false, pointId: null, pointType: null });
    toast.success("Start point and all subsequent points removed", { autoClose: 3000 });
  };

  const handleCancelDelete = () => setDeleteConfirmModal({ show: false, pointId: null, pointType: null });

  const clearAllPoints = () => {
    setPoints([]); setSelectedPointType("");
    setRoadSnappingEnabled(false); clearRouteState();
    setFromLocation(""); setToLocation(""); setRouteName("");
    toast.info("All points cleared");
  };

  // ── Wait time ─────────────────────────────────────────────────────────────
  const openWaitTimeModal = (pointId, currentWaitTime) =>
    setWaitTimeModal({ show: true, pointId, currentWaitTime });

  const updateWaitTime = (waitTime) => {
    setPoints((prev) => prev.map((p) => (p.id === waitTimeModal.pointId ? { ...p, waitTime } : p)));
    setWaitTimeModal({ show: false, pointId: null, currentWaitTime: 0 });
    toast.success(`Wait time updated to ${waitTime} minutes`);
  };

  // ── Save / Update ─────────────────────────────────────────────────────────
  const validateForm = () => {
    if (!routeName.trim()) { toast.error("Please enter a route name"); return false; }
    if (!selectedRegionId) { toast.error("Please select an administrative region"); return false; }
    if (!fromLocation.trim()) { toast.error("Please enter from location"); return false; }
    if (!toLocation.trim()) { toast.error("Please enter to location"); return false; }
    if (!points.length) { toast.error("Please add at least one point"); return false; }
    if (!points.some((p) => p.pointType === "start")) { toast.error("Please add a start point"); return false; }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const isEditingRoute = editMode || isEdit;
      let allRoutePoints = [];

      if (roadSnappingEnabled && snappedRouteCoordinates.length > 0) {
        allRoutePoints = snappedRouteCoordinates.map((coord, index) => ({
          createdBy: userId,
          created_on: new Date().toISOString().slice(0, 19).replace("T", " ") + ".000",
          latitude: coord[0], longitude: coord[1],
          pointType: index === 0 ? "start" : index === snappedRouteCoordinates.length - 1 ? "end" : "route_point",
          sequenceNo: index + 1, waitTime: 0, category: "route_coordinate",
        }));
        points.filter((p) => p.pointType === "halt").sort((a, b) => a.sequenceNo - b.sequenceNo).forEach((halt) => {
          let closest = 0, minDist = Infinity;
          snappedRouteCoordinates.forEach((coord, i) => {
            const d = haversine(halt.latitude, halt.longitude, coord[0], coord[1]);
            if (d < minDist) { minDist = d; closest = i; }
          });
          if (allRoutePoints[closest])
            allRoutePoints[closest] = { ...allRoutePoints[closest], pointType: "halt", waitTime: halt.waitTime, category: "halt_point" };
        });
      } else {
        allRoutePoints = [...points].sort((a, b) => a.sequenceNo - b.sequenceNo).map((p, i) => ({
          createdBy: userId, created_on: p.created_on,
          latitude: p.latitude, longitude: p.longitude,
          pointType: p.pointType, sequenceNo: i + 1, waitTime: p.waitTime, category: "original_point",
        }));
      }

      const payload = isEditingRoute
        ? allRoutePoints
        : {
          routeName: routeName.trim(), fromLocation: fromLocation.trim(), toLocation: toLocation.trim(),
          routePoints: allRoutePoints, totalCoordinates: allRoutePoints.length,
          routeType: roadSnappingEnabled ? "snapped_route" : "direct_route",
          regionId: selectedRegionId,
          ogcFid: selectedOgcFid,
        };

      let response;
      if (isEditingRoute) {
        const routeId = editMode ? editRouteData.routeId : routeData.routeId;
        response = await putDataWithBody(updateRoute.replace("ID", routeId), payload, {});
      } else {
        response = await postDataWithBody(createroutefleet, payload, {});
      }

      if (response?.status === "success") {
        toast.success(response.message || (isEditingRoute ? "Route updated successfully!" : "Route created successfully!"), { position: "top-center" });
        setRouteName(""); setFromLocation(""); setToLocation(""); setPoints([]);
        setSelectedPointType(""); setRoadSnappingEnabled(false);
        setSnappedRouteCoordinates([]); setPreviousSnappedRoute([]);
        setWaypoints([]); setHasModifiedRoute(false); setDragHistory([]);
        if (isEditingRoute && onUpdateSuccess) setTimeout(onUpdateSuccess, 500);
        if (!isEditingRoute) resetForm();
      } else {
        toast.error(response?.message || "Failed to save route");
      }
    } catch (err) {
      console.error("handleSave:", err);
      toast.error("Failed to save route. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setRouteName(""); setFromLocation(""); setToLocation(""); setPoints([]);
    setSelectedPointType(""); setRoadSnappingEnabled(false);
    setSnappedRouteCoordinates([]); setPreviousSnappedRoute([]);
    setWaypoints([]); setHasModifiedRoute(false);
    setRegionCheckedKeys([]); setDistrictPolygons([]);
    setSelectedOgcFid(null); setSelectedOgcType(''); setSelectedRegionId(null);
    if (mapRef.current) { mapRef.current.setCenter(DEFAULT_CENTER); mapRef.current.setZoom(12); }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const visiblePoints = points
    .filter((p) => p.pointType !== "route_point")
    .sort((a, b) => a.sequenceNo - b.sequenceNo)
    .map((p, i) => ({ ...p, sequenceNo: i + 1 }));

  const polylinePath = (() => {
    if (roadSnappingEnabled && snappedRouteCoordinates.length > 1)
      return snappedRouteCoordinates.map(([lat, lng]) => ({ lat, lng }));
    if (points.length < 2) return [];
    return [...points].sort((a, b) => a.sequenceNo - b.sequenceNo).map((p) => ({ lat: p.latitude, lng: p.longitude }));
  })();

  const pointCumDistances = visiblePoints.reduce((acc, point, i) => {
    if (i === 0) return [0];
    const prev = visiblePoints[i - 1];
    const d = haversine(parseFloat(prev.latitude), parseFloat(prev.longitude), parseFloat(point.latitude), parseFloat(point.longitude));
    return [...acc, acc[i - 1] + d];
  }, []);
  const totalRouteDistance = pointCumDistances.length > 0 ? pointCumDistances[pointCumDistances.length - 1] : 0;
  const totalRouteDuration = Math.round(totalRouteDistance / 40 * 60);

  // ═══════════════════════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="row g-3" style={{ marginTop: "0px", alignItems: "stretch" }}>
      <style>{infoWindowStyle}</style>

      {/* ─── Form Panel ──────────────────────────────────────────────────── */}
      <div className="col-md-4" style={{ marginTop: "0px", display: "flex", flexDirection: "column" }}>
        <div style={{
          flex: 1,
          display: "flex", flexDirection: "column",
          borderRadius: 12, overflow: "hidden",
          boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
          border: "1px solid #e2ecf5",
        }}>

          {/* ── Gradient Header ── */}
          <div style={{ background: "linear-gradient(to right, #0f3d3a 0%, #0f4a45 50%, #1a7a73 100%)", padding: "9px 14px", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <div style={{ width: 26, height: 26, borderRadius: 6, background: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {isEdit
                  ? <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>
                  : <><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>
                }
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{isEdit ? "Edit Route" : "Create Route"}</div>
              {editMode && editRouteData && <div style={{ fontSize: 10, color: "rgba(255,255,255,0.82)", marginTop: 1 }}>Editing: {editRouteData.routeName}</div>}
            </div>
          </div>

          {/* ── Scrollable Form Content ── */}
          <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10, padding: "12px", background: "#f4f7fb" }}>

          {/* ── Card 1: Basic Information ── */}
          <div style={{ background: "#fff", border: "1px solid #e8ecf1", borderRadius: 10, padding: "14px 16px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#0f4a45", color: "#fff", fontSize: 11, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>1</div>
              <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#fff8e1", border: "1.5px solid #f59e0b", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>Basic information</span>
              <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, #e2e8f0 0%, transparent 100%)" }} />
            </div>
            {/* Route Name */}
            <div style={{ marginBottom: 14 }}>
              <label htmlFor="routeName" style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 6 }}>
                Route Name <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input type="text" className="form-control" id="routeName" value={routeName}
                onChange={(e) => handleRouteNameChange(e, setRouteName, "Route Name")}
                placeholder="Enter route name" disabled={isEdit || editMode} required
                title="Enter a unique name for this route"
                style={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13, padding: "9px 12px" }} />
            </div>
            {/* ── Region Tree ── */}
            <div style={{ marginBottom: 0 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 6 }}>
                Administrative Region <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <div style={{ border: '1px solid #dee2e6', borderRadius: 8, background: '#fafafa' }}>
                <div
                  onClick={() => setRegionTreeCollapsed(c => !c)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', cursor: 'pointer', userSelect: 'none',
                    borderBottom: regionTreeCollapsed ? 'none' : '1px solid #dee2e6',
                    borderRadius: regionTreeCollapsed ? 8 : '8px 8px 0 0',
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 500, color: '#475569' }}>
                    {regionCheckedKeys.length > 0
                      ? `${regionCheckedKeys.length} region(s) selected`
                      : 'Select region'}
                  </span>
                  <span style={{ fontSize: 14, color: '#94a3b8' }}>
                    {regionTreeCollapsed ? '▼' : '▲'}
                  </span>
                </div>
                {!regionTreeCollapsed && (
                  <div style={{ padding: 10, maxHeight: 220, overflowY: 'auto', position: 'relative' }}>
                    {/* Region search */}
                    <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 7, marginBottom: 8, overflow: 'hidden' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 8px', flexShrink: 0 }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                      <input
                        type="text" placeholder="Search region…"
                        value={regionSearchText}
                        onChange={(e) => setRegionSearchText(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ flex: 1, border: 'none', outline: 'none', fontSize: 12, padding: '6px 4px', background: 'transparent', color: '#374151' }}
                      />
                      {regionSearchText && (
                        <button type="button" onClick={(e) => { e.stopPropagation(); setRegionSearchText(''); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '0 8px', fontSize: 14 }}>✕</button>
                      )}
                    </div>
                    {regionCheckLoading && (
                      <div style={{
                        position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.7)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10,
                        borderRadius: '0 0 8px 8px',
                      }}>
                        <span className="spinner-border spinner-border-sm text-info me-2" />
                        <small className="text-muted">Loading…</small>
                      </div>
                    )}
                    {regionTreeData.length === 0 ? (
                      <small className="text-muted">Loading regions…</small>
                    ) : (
                      <Tree
                        treeData={regionSearchText.trim()
                          ? regionTreeData.filter(n => n.title?.toLowerCase().includes(regionSearchText.toLowerCase()))
                          : regionTreeData}
                        checkable
                        checkStrictly
                        checkedKeys={regionCheckedKeys}
                        expandedKeys={regionExpandedKeys}
                        onExpand={setRegionExpandedKeys}
                        onCheck={onRegionCheck}
                        loadData={loadRegionChildren}
                        showLine
                      />
                    )}
                  </div>
                )}
              </div>
              {regionCheckedKeys.length > 0 && selectedRegionId && (
                <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 6 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  <small style={{ fontSize: 11, color: "#22c55e", fontWeight: 600 }}>Region shown on map</small>
                </div>
              )}
            </div>
          </div>

          {/* ── Card 2: Route Endpoints ── */}
          <div style={{ background: "#fff", border: "1px solid #e8ecf1", borderRadius: 10, padding: "14px 16px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#0f4a45", color: "#fff", fontSize: 11, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>2</div>
              <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#ecfdf5", border: "1.5px solid #10b981", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="12" r="2"/><circle cx="19" cy="12" r="2"/><path d="M7 12h10"/></svg>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>Route endpoints</span>
              <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, #e2e8f0 0%, transparent 100%)" }} />
            </div>
            <div style={{ display: "flex", gap: 10, marginBottom: 0 }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#22c55e", flexShrink: 0, display: "inline-block" }} />
                  From <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input type="text" className="form-control" value={fromLocation}
                  onChange={(e) => handleLocationChange(e, setFromLocation, "From Location")}
                  placeholder="Start location" disabled={roadSnappingEnabled || isEdit || editMode} required
                  title="Select the starting point of the route"
                  style={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13, padding: "9px 12px" }} />
                <small style={{ fontSize: 10, color: "#94a3b8", marginTop: 3, display: "block" }}>Starting point — search or click map</small>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#ef4444", flexShrink: 0, display: "inline-block" }} />
                  To <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input type="text" className="form-control" value={toLocation}
                  onChange={(e) => handleLocationChange(e, setToLocation, "To Location")}
                  placeholder="End location"
                  disabled={!points.some((p) => p.pointType === "start") || roadSnappingEnabled || isEdit || editMode} required
                  title="Select the destination point of the route"
                  style={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13, padding: "9px 12px" }} />
                <small style={{ fontSize: 10, color: "#94a3b8", marginTop: 3, display: "block" }}>Destination — add Start point first</small>
              </div>
            </div>
          </div>

          {/* ── Card 3: Map Points ── */}
          <div style={{ background: "#fff", border: "1px solid #e8ecf1", borderRadius: 10, padding: "14px 16px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#0f4a45", color: "#fff", fontSize: 11, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>3</div>
              <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#fff7ed", border: "1.5px solid #f97316", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>Map points</span>
              <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, #e2e8f0 0%, transparent 100%)" }} />
            </div>
            {/* Point Type Selector */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 8 }}>
                Select Point Type
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                {["start", "halt", "end"].map((type) => {
                  const cfgMap = {
                    start: { c: "#22c55e", tint: "#f0fdf4", label: "Start" },
                    halt:  { c: "#f59e0b", tint: "#fffbeb", label: "Halt" },
                    end:   { c: "#ef4444", tint: "#fef2f2", label: "End" },
                  };
                  const cfg = cfgMap[type];
                  const enabled = isPointTypeEnabled(type) && !roadSnappingEnabled;
                  const isSelected = selectedPointType === type;
                  const done = !enabled && points.some((p) => p.pointType === type);
                  const iconColor = isSelected ? "#fff" : enabled ? cfg.c : "#94a3b8";
                  return (
                    <button key={type} type="button"
                      className="point-type-btn"
                      onClick={() => enabled && setSelectedPointType(type)}
                      style={{
                        flex: 1, padding: "12px 4px 10px", borderRadius: 10,
                        border: `1.5px solid ${isSelected ? cfg.c : enabled ? cfg.c + "70" : "#e2e8f0"}`,
                        background: isSelected ? cfg.c : enabled ? cfg.tint : "#f8fafc",
                        color: isSelected ? "#fff" : enabled ? cfg.c : "#94a3b8",
                        fontSize: 11, fontWeight: 700, cursor: enabled ? "pointer" : "default",
                        display: "flex", flexDirection: "column", alignItems: "center", gap: 5,
                      }}>
                      {type === "start" && (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill={iconColor} strokeWidth="0">
                          <polygon points="5 3 19 12 5 21 5 3"/>
                        </svg>
                      )}
                      {type === "halt" && (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill={iconColor} strokeWidth="0">
                          <rect x="6" y="4" width="4" height="16" rx="1.5"/>
                          <rect x="14" y="4" width="4" height="16" rx="1.5"/>
                        </svg>
                      )}
                      {type === "end" && (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill={iconColor} strokeWidth="0">
                          <rect x="4" y="4" width="16" height="16" rx="3"/>
                        </svg>
                      )}
                      <span style={{ fontSize: 11 }}>{cfg.label}{done ? " ✓" : ""}</span>
                    </button>
                  );
                })}
              </div>
              {roadSnappingEnabled && (
                <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 6 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0f4a45" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <small style={{ fontSize: 11, color: "#0f4a45" }}>Drag blue points on the map to adjust the route</small>
                </div>
              )}
              {!roadSnappingEnabled && selectedPointType && isPointTypeEnabled(selectedPointType) && (
                <small style={{ fontSize: 11, color: "#64748b", marginTop: 5, display: "block" }}>Click on the map to add a {selectedPointType} point</small>
              )}
              {selectedPointType && !isPointTypeEnabled(selectedPointType) && !roadSnappingEnabled && (
                <small style={{ fontSize: 11, color: "#f59e0b", marginTop: 5, display: "block" }}>Complete previous steps first</small>
              )}
            </div>
            {/* Route Points List */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.4px", margin: 0 }}>
                  Route Points <span style={{ background: "#ccfbf1", color: "#0f4a45", padding: "1px 7px", borderRadius: 10, fontSize: 10, fontWeight: 700, marginLeft: 4 }}>{visiblePoints.length}</span>
                </label>
                {points.length > 0 && (
                  <button type="button" onClick={clearAllPoints}
                    style={{ background: "none", border: "1px solid #fecaca", borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 600, color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                    Clear
                  </button>
                )}
              </div>
              <div className="route-scrollbar" style={{ border: "1px solid #e2e8f0", borderRadius: 10, maxHeight: 200, overflowY: "auto", background: "#fafbfc" }}>
                {visiblePoints.length === 0 ? (
                  <div style={{ padding: "18px 12px", textAlign: "center" }}>
                    <div style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>No points added yet</div>
                  </div>
                ) : (
                  visiblePoints.map((point, idx) => {
                    const ptColors = { start: { c: "#22c55e", tint: "#f0fdf4" }, halt: { c: "#f59e0b", tint: "#fffbeb" }, end: { c: "#ef4444", tint: "#fef2f2" } };
                    const pc = ptColors[point.pointType] || { c: "#64748b", tint: "#f8fafc" };
                    return (
                      <div key={point.id} className="route-point-item" style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 10px", borderBottom: idx < visiblePoints.length - 1 ? "1px solid #f1f5f9" : "none", background: "#fff" }}>
                        <div style={{ width: 30, height: 30, borderRadius: "50%", background: pc.tint, border: `2px solid ${pc.c}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {point.pointType === "start" && <svg width="10" height="10" viewBox="0 0 24 24" fill={pc.c} strokeWidth="0"><polygon points="5 3 19 12 5 21 5 3"/></svg>}
                          {point.pointType === "halt" && <svg width="10" height="10" viewBox="0 0 24 24" fill={pc.c} strokeWidth="0"><rect x="6" y="4" width="4" height="16" rx="1.5"/><rect x="14" y="4" width="4" height="16" rx="1.5"/></svg>}
                          {point.pointType === "end" && <svg width="10" height="10" viewBox="0 0 24 24" fill={pc.c} strokeWidth="0"><rect x="4" y="4" width="16" height="16" rx="3"/></svg>}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {point.isLoadingAddress ? (
                              <><span className="spinner-border spinner-border-sm me-1" style={{ width: 10, height: 10 }} />Loading…</>
                            ) : (
                              (point.address || `${parseFloat(point.latitude).toFixed(5)}, ${parseFloat(point.longitude).toFixed(5)}`).split(",")[0]
                            )}
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
                            <span style={{ fontSize: 10, color: pc.c, fontWeight: 600 }}>
                              {point.pointType === "start" ? "Start point" : point.pointType === "halt" ? "Halt" : "End point"}
                            </span>
                            <span style={{ fontSize: 10, color: "#cbd5e1" }}>·</span>
                            <span style={{ fontSize: 10, color: "#94a3b8" }}>{(pointCumDistances[idx] || 0).toFixed(1)} km</span>
                            {point.pointType === "halt" && (
                              <button type="button" onClick={() => openWaitTimeModal(point.id, point.waitTime)} disabled={roadSnappingEnabled}
                                style={{ background: "#fffbeb", border: "1px solid #f59e0b80", borderRadius: 4, padding: "1px 6px", fontSize: 9, fontWeight: 600, color: "#d97706", cursor: "pointer" }}>
                                {point.waitTime}min
                              </button>
                            )}
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <button type="button" onClick={() => removePoint(point.id)} disabled={roadSnappingEnabled}
                            title={roadSnappingEnabled ? "Clear route to remove points" : "Remove point"}
                            style={{ width: 20, height: 20, borderRadius: "50%", background: "#fef2f2", border: "1px solid #fecaca", cursor: roadSnappingEnabled ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444", fontSize: 12, fontWeight: 700, padding: 0, flexShrink: 0 }}>×</button>
                          <div style={{ display: "flex", flexDirection: "column", gap: 2.5, padding: "0 2px", cursor: "default", opacity: 0.35 }}>
                            {[0,1,2].map(r => (
                              <div key={r} style={{ display: "flex", gap: 2.5 }}>
                                <span style={{ width: 3, height: 3, borderRadius: "50%", background: "#64748b", display: "block" }} />
                                <span style={{ width: 3, height: 3, borderRadius: "50%", background: "#64748b", display: "block" }} />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              {/* Stats Bar */}
              {visiblePoints.length >= 2 && (
                <div style={{ display: "flex", borderTop: "1px solid #e2e8f0", borderRadius: "0 0 10px 10px", background: "#f8fafc", overflow: "hidden" }}>
                  <div style={{ flex: 1, padding: "8px 6px", textAlign: "center", borderRight: "1px solid #e2e8f0" }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.6px" }}>Distance</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#1e293b", marginTop: 2 }}>{totalRouteDistance.toFixed(1)} km</div>
                  </div>
                  <div style={{ flex: 1, padding: "8px 6px", textAlign: "center", borderRight: "1px solid #e2e8f0" }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.6px" }}>Duration</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#1e293b", marginTop: 2 }}>{totalRouteDuration} min</div>
                  </div>
                  <div style={{ flex: 1, padding: "8px 6px", textAlign: "center" }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.6px" }}>Points</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#1e293b", marginTop: 2 }}>{visiblePoints.length}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          </div>{/* end scrollable form content */}

          {/* ── Sticky Action Footer ── */}
          <div style={{ flexShrink: 0, padding: "10px 14px", background: "#fff", borderTop: "1.5px solid #e2ecf5", display: "flex", flexDirection: "column", gap: 8 }}>

          {/* ── Create Route Button ── */}
          {isCreateRouteEnabled() && (
            <button type="button"
              className={roadSnappingEnabled ? "btn-route-success" : "btn-route-primary"}
              onClick={toggleRoadSnapping} disabled={isSnapping}
              style={{
                width: "100%", padding: "10px", borderRadius: 10, border: "none", fontSize: 13, fontWeight: 700, cursor: isSnapping ? "not-allowed" : "pointer",
                background: roadSnappingEnabled ? "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)" : "linear-gradient(to right, #0f3d3a 0%, #0f4a45 50%, #1a7a73 100%)",
                color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
              }}>
              {isSnapping ? (
                <><span className="spinner-border spinner-border-sm" style={{ width: 14, height: 14, borderWidth: 2 }} />Snapping to roads…</>
              ) : (
                <>
                  {/* <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    {roadSnappingEnabled
                      ? <><polyline points="20 6 9 17 4 12"/></>
                      : <><path d="M3 17l4-4 4 4 4-6 4 2"/></>
                    }
                  </svg> */}
                  {roadSnappingEnabled ? "Route Created ✓" : "Create Route"}
                </>
              )}
            </button>
          )}

          {/* ── Save / Cancel Buttons ── */}
          {points.length >= 2 && !roadSnappingEnabled && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <small style={{ fontSize: 11, color: "#d97706" }}>Click "Create Route" first to enable Save</small>
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            {!isEdit && !editMode && (
              <button type="button"
                className="btn-route-secondary"
                onClick={() => {
                  setRouteName(""); setFromLocation(""); setToLocation(""); setPoints([]);
                  setSelectedPointType(""); setRoadSnappingEnabled(false);
                  setSnappedRouteCoordinates([]); setPreviousSnappedRoute([]);
                  setWaypoints([]); setHasModifiedRoute(false);
                  setRegionCheckedKeys([]); setDistrictPolygons([]);
                  setSelectedOgcFid(null); setSelectedOgcType(''); setSelectedRegionId(null);
                }}
                style={{ padding: "10px 18px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                Cancel
              </button>
            )}
            <button type="button"
              className="btn-route-success"
              onClick={handleSave}
              disabled={isLoading || !isCreateRouteEnabled() || !roadSnappingEnabled}
              style={{
                flex: 1, padding: "10px", borderRadius: 10, border: "none", fontSize: 13, fontWeight: 700,
                background: isLoading || !isCreateRouteEnabled() || !roadSnappingEnabled
                  ? "#94a3b8"
                  : "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                color: "#fff", cursor: isLoading || !isCreateRouteEnabled() || !roadSnappingEnabled ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                boxShadow: isLoading || !isCreateRouteEnabled() || !roadSnappingEnabled ? "none" : "0 3px 10px rgba(34,197,94,0.30)",
              }}>
              {isLoading
                ? <><span className="spinner-border spinner-border-sm" style={{ width: 14, height: 14, borderWidth: 2 }} />{isEdit ? "Updating…" : "Saving…"}</>
                : <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                      <polyline points="17 21 17 13 7 13 7 21"/>
                      <polyline points="7 3 7 8 15 8"/>
                    </svg>
                    {isEdit ? "Update Route" : "Save route"}
                  </>
              }
            </button>
          </div>
          </div>{/* end sticky action footer */}

        </div>{/* end sticky container */}
      </div>

      {/* ─── Map Panel ───────────────────────────────────────────────────── */}
      <div className="col-md-8" style={{ position: "relative", zIndex: 1, marginTop: "0px" }}>
        <div className="card border-0 route-map-card" style={{ boxShadow: "0 2px 16px rgba(0,0,0,0.10)", borderRadius: 12, overflow: "hidden" }}>
          {/* Map Header */}
          <div style={{ background: "linear-gradient(to right, #0f3d3a 0%, #0f4a45 50%, #1a7a73 100%)", padding: "7px 14px 6px", display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 26, height: 26, borderRadius: 6, background: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>Route Map</div>
              {!roadSnappingEnabled && selectedPointType && isPointTypeEnabled(selectedPointType) && (
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.82)", marginTop: 1 }}>Click on the map to add a {selectedPointType} point</div>
              )}
              {roadSnappingEnabled && (
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.82)", marginTop: 1, display: "flex", alignItems: "center", gap: 6 }}>
                  Drag blue points to adjust the route path
                  {hasModifiedRoute && <span style={{ background: "#f59e0b", color: "#fff", padding: "1px 7px", borderRadius: 8, fontSize: 9, fontWeight: 700 }}>Modified</span>}
                </div>
              )}
            </div>
            <span style={{ background: "rgba(255,255,255,0.18)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 20, padding: "2px 9px", fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
              {(LAYERS.find(l => l.id === selectedLayer)?.label ?? selectedLayer)} layer
            </span>
          </div>

          {/* Search bar */}
          <div style={{ padding: "10px 14px 0", position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 9, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
              <div style={{ padding: "0 10px", color: "#94a3b8", display: "flex", alignItems: "center" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </div>
              <input type="text" placeholder="Search city, area, landmark or coordinates…"
                value={searchQuery} onChange={handleSearchInputChange}
                style={{ flex: 1, border: "none", outline: "none", fontSize: 13, padding: "8px 4px", color: "#1e293b", background: "transparent" }} />
              {searchQuery && (
                <button type="button" onClick={clearSearch}
                  style={{ padding: "0 10px", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 16, display: "flex", alignItems: "center" }}>✕</button>
              )}
              {isSearching && (
                <div style={{ padding: "0 10px" }}>
                  <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: 14, height: 14, borderWidth: 2 }} />
                </div>
              )}
            </div>
            {searchResults.length > 0 && (
              <div style={{ position: "absolute", left: 14, right: 14, top: "calc(100% - 2px)", zIndex: 1050, background: "#fff", borderRadius: "0 0 10px 10px", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", border: "1px solid #e2e8f0", borderTop: "none", overflow: "hidden" }}>
                {searchResults.map((result) => (
                  <button key={result.id} type="button" onClick={() => selectSearchResult(result)}
                    className="search-result-row"
                    style={{ width: "100%", padding: "10px 14px", background: "none", border: "none", borderBottom: "1px solid #f1f5f9", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2, flexShrink: 0 }}><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{result.name.split(",")[0]}</div>
                      <div style={{ fontSize: 11, color: "#64748b" }}>{result.name.split(",").slice(1, 3).join(",")}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Map */}
          <div className="card-body p-0 position-relative">
            {isLoading && <LoadingOverlay message="Loading map data..." />}
            {isGeocoding && <LoadingOverlay message="Finding location on map..." />}
            {isSnapping && <LoadingOverlay message="Adjusting route..." />}

            {roadSnappingEnabled && hasModifiedRoute && (
              <button type="button" className="btn btn-warning shadow-lg"
                onClick={resetToPreviousRoute} disabled={isSnapping} title="Undo last drag"
                style={{
                  position: "absolute", top: 150, right: 20, zIndex: 1000,
                  width: 35, height: 35, borderRadius: "45%",
                  display: "flex", justifyContent: "center", alignItems: "center",
                }}>
                <i className="fas fa-undo" style={{ fontSize: 18 }} />
              </button>
            )}

            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={MAP_CONTAINER_STYLE}
                center={DEFAULT_CENTER}
                zoom={12}
                options={{ ...MAP_OPTIONS, mapTypeId: mapTypes[selectedLayer], minZoom: 4 }}
                onLoad={onMapLoad}
                onClick={onGoogleMapClick}
              >
                {/* Region polygons */}
                {districtPolygons.flatMap((dp, i) => {
                  const clr = polygonColorByLevel(dp.level ?? 1);
                  return dp.paths.map((path, j) => (
                    <Polygon
                      key={`dp-${i}-${j}`}
                      paths={path}
                      options={{
                        strokeColor: clr.stroke,
                        strokeOpacity: 0.9,
                        strokeWeight: dp.level === 0 ? 3 : 2,
                        fillColor: clr.fill,
                        fillOpacity: clr.opacity,
                      }}
                      onClick={onGoogleMapClick}
                    />
                  ));
                })}


                {/* Route polyline */}
                {polylinePath.length > 1 && (
                  <Polyline
                    path={polylinePath}
                    options={{
                      strokeColor: roadSnappingEnabled ? "#1a7a73" : "#6c757d",
                      strokeWeight: roadSnappingEnabled ? 4 : 3,
                      strokeOpacity: 0.7,
                      ...(roadSnappingEnabled ? {} : {
                        icons: [{ icon: { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 4 }, offset: "0", repeat: "20px" }],
                      }),
                    }}
                  />
                )}

                {/* Start / Halt / End markers */}
                {points.filter((p) => p.pointType !== "route_point").map((point) => (
                  <Marker
                    key={point.id}
                    position={{ lat: point.latitude, lng: point.longitude }}
                    icon={createCustomIcon(getIconColor(point.pointType), point.pointType)}
                    draggable={false}
                    onClick={() => setActiveMarkerId(point.id)}
                  >
                    {activeMarkerId === point.id && (() => {
                      const ptCfg = {
                        start: { hdr: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)", tint: "#f0fdf4", c: "#22c55e", label: "Start Point", sub: "Route starting location" },
                        halt:  { hdr: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)", tint: "#fffbeb", c: "#f59e0b", label: "Halt Point",  sub: "Scheduled stop location" },
                        end:   { hdr: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)", tint: "#fef2f2", c: "#ef4444", label: "End Point",   sub: "Route ending location" },
                      };
                      const pc = ptCfg[point.pointType] || ptCfg.halt;
                      return (
                        <InfoWindow onCloseClick={() => setActiveMarkerId(null)} options={{ disableAutoPan: false, maxWidth: 280 }}>
                          <div style={{ minWidth: 250, maxWidth: 280, overflow: "hidden", fontFamily: "'Segoe UI', sans-serif" }}>
                            {/* Gradient header */}
                            <div style={{ background: pc.hdr, padding: "11px 14px 10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                              <div style={{ width: 32, height: 32, borderRadius: 7, background: "rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                {point.pointType === "halt"
                                  ? <svg width="15" height="15" viewBox="0 0 24 24" fill="white"><rect x="6" y="4" width="4" height="16" rx="2"/><rect x="14" y="4" width="4" height="16" rx="2"/></svg>
                                  : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5" fill="white" stroke="none"/></svg>
                                }
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{pc.label}</div>
                                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.82)", marginTop: 1 }}>{pc.sub}</div>
                              </div>
                              <button onClick={() => setActiveMarkerId(null)} style={{ width: 20, height: 20, borderRadius: "50%", background: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.35)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 10, fontWeight: 700, padding: 0, flexShrink: 0 }}>✕</button>
                            </div>
                            {/* Body */}
                            <div style={{ padding: "11px 14px 13px", background: "#fff" }}>
                              {point.pointType === "halt" && (
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9 }}>
                                  <div style={{ width: 24, height: 24, borderRadius: 5, background: pc.tint, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={pc.c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                  </div>
                                  <div>
                                    <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 1 }}>Wait Duration</div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                      <span style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>{point.waitTime}</span>
                                      <span style={{ fontSize: 10, color: "#64748b", background: "#fffbeb", borderRadius: 3, padding: "1px 5px", fontWeight: 600 }}>mins</span>
                                    </div>
                                  </div>
                                </div>
                              )}
                              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 11 }}>
                                <div style={{ width: 24, height: 24, borderRadius: 5, background: pc.tint, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={pc.c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>Location</div>
                                  <div style={{ fontSize: 11, color: "#475569", background: pc.tint, borderRadius: 5, padding: "5px 8px", borderLeft: `3px solid ${pc.c}`, lineHeight: 1.4 }}>
                                    {point.isLoadingAddress ? "Loading address…" : point.address || `${parseFloat(point.latitude).toFixed(5)}, ${parseFloat(point.longitude).toFixed(5)}`}
                                  </div>
                                </div>
                              </div>
                              <div style={{ display: "flex", gap: 6 }}>
                                {point.pointType === "halt" && (
                                  <button onClick={() => { openWaitTimeModal(point.id, point.waitTime); setActiveMarkerId(null); }}
                                    disabled={roadSnappingEnabled}
                                    style={{ flex: 1, padding: "7px 6px", borderRadius: 7, border: `1px solid ${pc.c}`, background: pc.tint, color: pc.c, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                                    Change Wait Time
                                  </button>
                                )}
                                <button onClick={() => { removePoint(point.id); setActiveMarkerId(null); }}
                                  disabled={roadSnappingEnabled}
                                  style={{ flex: 1, padding: "7px 6px", borderRadius: 7, border: "none", background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                                  Remove Point
                                </button>
                              </div>
                            </div>
                          </div>
                        </InfoWindow>
                      );
                    })()}
                  </Marker>
                ))}

                {/* Draggable waypoint handles */}
                {roadSnappingEnabled && waypoints.map((wp) => (
                  <Marker
                    key={wp.id}
                    position={{ lat: wp.position[0], lng: wp.position[1] }}
                    icon={createCustomIcon("#17a2b8", "waypoint")}
                    draggable={true}
                    onClick={() => setActiveMarkerId(wp.id)}
                    onDragEnd={(e) => handleWaypointDragEnd(wp.id, [e.latLng.lat(), e.latLng.lng()])}
                  >
                    {activeMarkerId === wp.id && (
                      <InfoWindow onCloseClick={() => setActiveMarkerId(null)} options={{ maxWidth: 180 }}>
                        <div style={{ padding: "4px 6px 6px", position: "relative" }}>
                          <button onClick={() => setActiveMarkerId(null)}
                            style={{ position: "absolute", top: 0, right: 0, background: "none", border: "none", cursor: "pointer", fontSize: 16, lineHeight: 1, color: "#dc3545", padding: "2px 4px", fontWeight: "bold" }}
                            title="Close">✕</button>
                          <h6 style={{ fontWeight: "bold", fontSize: 13, paddingRight: 18, marginBottom: 4 }}>Route Waypoint</h6>
                          <small style={{ color: "#666", fontSize: 11 }}>Drag to adjust the route path</small>
                        </div>
                      </InfoWindow>
                    )}
                  </Marker>
                ))}

                {/* ── Transit layer ── */}
                {showTransit && <TransitLayer />}

                {/* ── Map controls column — right side ── */}
                <div style={{ position: "absolute", top: 60, right: 10, zIndex: 1000, display: "flex", flexDirection: "column", gap: 8 }}>

                  {/* Layer Switcher */}
                  <div style={{ position: "relative" }}>
                    <button
                      onClick={() => setShowLayerControl((p) => !p)}
                      title="Map layers"
                      style={{ width: 40, height: 40, borderRadius: 10, background: showLayerControl ? "#1a7a73" : "#fff", border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                    >
                      <LayersIcon color={showLayerControl ? "#fff" : undefined} />
                    </button>
                    {showLayerControl && (
                      <div style={{ position: "absolute", top: 46, right: 0, background: "#fff", borderRadius: 10, padding: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.14)", minWidth: 150, border: "1px solid #e2e8f0" }}>
                        {LAYERS.map((l) => (
                          <div key={l.id} onClick={() => { setSelectedLayer(l.id); setShowLayerControl(false); }}
                            style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 10px", borderRadius: 7, cursor: "pointer", background: selectedLayer === l.id ? "#f0fdfb" : "transparent", transition: "background 0.15s" }}
                          >
                            <span style={{ color: selectedLayer === l.id ? "#1a7a73" : "#94a3b8" }}>{l.icon}</span>
                            <span style={{ fontSize: 13, fontWeight: selectedLayer === l.id ? 600 : 400, color: selectedLayer === l.id ? "#1a7a73" : "#334155" }}>{l.label}</span>
                            {selectedLayer === l.id && <span style={{ marginLeft: "auto", width: 7, height: 7, borderRadius: "50%", background: "#1a7a73" }} />}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Transit layer toggle */}
                  <button onClick={() => setShowTransit(p => !p)} title={showTransit ? "Hide transit" : "Show transit"}
                    style={{ width: 40, height: 40, borderRadius: 10, background: showTransit ? "#1a7a73" : "#fff", border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={showTransit ? "#fff" : "#555"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="4" y="2" width="16" height="16" rx="2"/>
                      <path d="M4 10h16"/><path d="M8 2v8"/><path d="M16 2v8"/>
                      <path d="M8 18l-2 4"/><path d="M16 18l2 4"/>
                    </svg>
                  </button>

                  {/* Zoom to fit route */}
                  {points.length > 0 && (
                    <button onClick={handleZoomToRoute} title="Zoom to fit route"
                      style={{ width: 40, height: 40, borderRadius: 10, background: "#fff", border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                      </svg>
                    </button>
                  )}
                </div>
              </GoogleMap>
            ) : (
              <div style={MAP_CONTAINER_STYLE} className="d-flex align-items-center justify-content-center bg-light">
                <div className="spinner-border text-info" role="status">
                  <span className="visually-hidden">Loading map…</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Wait Time Modal ─────────────────────────────────────────────── */}
      {waitTimeModal.show && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)" }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 360 }}>
            <div className="modal-content route-modal-content" style={{ border: "none", borderRadius: 14, overflow: "hidden", boxShadow: "0 24px 64px rgba(0,0,0,0.28)" }}>
              {/* Header */}
              <div style={{ background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)", padding: "16px 18px", display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Set Wait Time</div>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.82)", marginTop: 1 }}>Choose wait duration for this halt point</div>
                </div>
                <button onClick={() => setWaitTimeModal({ show: false, pointId: null, currentWaitTime: 0 })}
                  style={{ width: 26, height: 26, borderRadius: "50%", background: "rgba(255,255,255,0.22)", border: "1px solid rgba(255,255,255,0.35)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 12, fontWeight: 700, padding: 0 }}>✕</button>
              </div>
              {/* Options */}
              <div style={{ padding: "18px 18px 20px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {[5, 10, 15, 20].map((min) => {
                  const isActive = waitTimeModal.currentWaitTime === min;
                  return (
                    <button key={min} type="button" onClick={() => updateWaitTime(min)}
                      style={{
                        padding: "14px 8px", borderRadius: 10,
                        border: `1.5px solid ${isActive ? "#f59e0b" : "#e2e8f0"}`,
                        background: isActive ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)" : "#fff",
                        color: isActive ? "#fff" : "#475569",
                        fontSize: 13, fontWeight: 700, cursor: "pointer",
                        display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                        boxShadow: isActive ? "0 4px 12px rgba(245,158,11,0.35)" : "none",
                      }}>
                      <span style={{ fontSize: 22, fontWeight: 800, lineHeight: 1 }}>{min}</span>
                      <span style={{ fontSize: 10, fontWeight: 600, opacity: 0.85 }}>minutes</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Delete Confirm Modal ────────────────────────────────────────── */}
      {deleteConfirmModal.show && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)" }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
            <div className="modal-content route-modal-content" style={{ border: "none", borderRadius: 14, overflow: "hidden", boxShadow: "0 24px 64px rgba(0,0,0,0.28)" }}>
              {/* Header */}
              <div style={{ background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)", padding: "16px 18px", display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Confirm Deletion</div>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.82)", marginTop: 1 }}>This action cannot be undone</div>
                </div>
                <button onClick={handleCancelDelete}
                  style={{ width: 26, height: 26, borderRadius: "50%", background: "rgba(255,255,255,0.22)", border: "1px solid rgba(255,255,255,0.35)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 12, fontWeight: 700, padding: 0 }}>✕</button>
              </div>
              {/* Body */}
              <div style={{ padding: "18px 20px" }}>
                <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 9, padding: "12px 14px", display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  <div style={{ fontSize: 12, color: "#92400e", lineHeight: 1.6 }}>
                    Removing the start point will <strong>delete all route points</strong>. This action cannot be undone.
                  </div>
                </div>
              </div>
              {/* Footer */}
              <div style={{ padding: "0 20px 20px", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" className="btn-route-secondary" onClick={handleCancelDelete}
                  style={{ padding: "9px 20px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  Cancel
                </button>
                <button type="button" className="btn-route-danger" onClick={handleConfirmDelete}
                  style={{ padding: "9px 22px", borderRadius: 8, border: "none", background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  Yes, Remove All Points
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateRoute;